import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {alpha, CircularProgress, Grid, ThemeProvider, useTheme} from "@mui/material";
import {whiteInputsTheme} from "../../../util/theme/whiteInputsTheme";
import CustomDialog from "../../shared/custom-dialog/CustomDialog";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import {CheckCircleOutlined, ErrorOutline} from "@mui/icons-material";
import Button from "@mui/material/Button";
import useQueryParams from "../../../hooks/useQueryParams";
import {RootState, useAppSelector} from "../../../store";
import PaymentService from "../../../api/paymentService";

interface ICheckOrder {
    display: boolean
    error: boolean
    title?: string
    message?: string
    hint?: string
    order_type?: "voucher" | "draw"
    payment_required?: boolean
}


const PurchaseConfirmationDialog = () => {
    const {t} = useTranslation()
    const {params, remove} = useQueryParams()
    const order = params.get("order")
    const isLoggedIn = useAppSelector((state: RootState) => !!state.authReducer.loggedInUser.id);
    const [content, setContent] = useState<ICheckOrder>({display: false, error: false});
    const [loading, setLoading] = useState<boolean>(false);
    const theme = useTheme()

    const handleDialogClose = useCallback(() => {
        remove("order")
        setContent({display: false, error: false})
    }, [remove])

    useEffect(() => {
        if (order && isLoggedIn) {
            setLoading(true);
            PaymentService.checkOrder(order).then(res => setContent(res.data))
                .catch(() => handleDialogClose())
                .finally(() => setLoading(false));
        }
    }, [order, handleDialogClose, isLoggedIn]);

    const isDrawPurchase = content.order_type === "draw"
    const hasPaymentStep = content.payment_required !== false
    const steps = useMemo(() => {
        const purchaseSteps = []
        if (isDrawPurchase) {
            purchaseSteps.push(t("question_step"))
        }
        purchaseSteps.push(t("details"))
        if (hasPaymentStep) {
            purchaseSteps.push(t("payment"))
        }
        purchaseSteps.push(t("confirmation"))
        return purchaseSteps
    }, [hasPaymentStep, isDrawPurchase, t])

    const detailsStepIndex = isDrawPurchase ? 1 : 0
    const paymentStepIndex = hasPaymentStep ? detailsStepIndex + 1 : detailsStepIndex
    const confirmationStepIndex = steps.length - 1
    const activeStep = content.error ? paymentStepIndex : confirmationStepIndex

    const confirmationStepContent = (
        <Box textAlign="center">
            {loading ? <CircularProgress size={80} color='primary'/> : <>
                {content.error ? <ErrorOutline color="primary" sx={{fontSize: "6rem"}}/> :
                    <CheckCircleOutlined color="primary" sx={{fontSize: "6rem"}}/>}
                <Typography variant="h5" color="primary"
                            fontWeight={600}>{content.title}</Typography>
                <Typography variant="subtitle1" color="primary">{content.message}</Typography>
                <Typography variant="body2"
                            sx={{color: alpha(theme.palette.primary.main, .6)}}>{content.hint}</Typography>
            </>}

        </Box>
    )

    const dialogContent = (
        <Box>
            <Stepper variant="outlined" activeStep={activeStep} alternativeLabel sx={{mb: 4}}>
                {steps.map((label, idx) => (
                    <Step key={label}>
                        <StepLabel error={content.error && idx === paymentStepIndex}>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>
            {confirmationStepContent}
        </Box>

    )

    const dialogActions = (
        <Grid container justifyContent="flex-end" alignItems="center">
            <Button
                variant="contained"
                onClick={handleDialogClose}
            >
                {t('close')}
            </Button>
        </Grid>

    )

    return (
        <ThemeProvider theme={whiteInputsTheme}>
            <CustomDialog open={content.display}
                          title={t("confirmation")}
                          maxWidth='sm'
                          actions={dialogActions}
                          handleDialogClose={handleDialogClose}
                          content={dialogContent}/>
        </ThemeProvider>
    )
}

export default PurchaseConfirmationDialog
